import { cloneElement } from 'react';
import type { ReactElement } from 'react';
import type { ButtonProps } from 'antd';
import * as FileSaver from 'file-saver';
import writeXlsxFile from 'write-excel-file/browser';
import { useCustomMessage } from '../../../../hooks';

type ExportRow = Record<string, unknown>;
type ExportHeader = { readonly label: string; readonly key: string };

/** Props for exporting rows as XLSX, CSV, or tab-delimited text. */
export interface ExportToFormatProps {
  readonly children: ReactElement<ButtonProps>;
  readonly data: readonly ExportRow[];
  readonly headers: readonly ExportHeader[];
  readonly filename: string;
  readonly format: 'xlsx' | 'csv' | 'txt';
  readonly onLoading?: (isLoading: boolean) => void;
}

const cellToString = (value: unknown): string => {
  if (value == null) return '';
  if (value instanceof Date) return value.toISOString();
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return String(value);
  }
  try {
    return JSON.stringify(value) ?? '';
  } catch {
    return '[Unserializable value]';
  }
};

const ExportToFormat = (props: Readonly<ExportToFormatProps>) => {
  const { data, filename, children, headers, format, onLoading } = props;
  const { showMessageError } = useCustomMessage();

  const buildRows = (): unknown[][] => [
    headers.map((header) => header.label),
    ...data.map((row) => headers.map((header) => row[header.key])),
  ];

  const buildXlsxBlob = () => {
    const rows = buildRows().map((row) =>
      row.map((value) => {
        if (value == null) return null;
        if (
          value instanceof Date ||
          typeof value === 'string' ||
          typeof value === 'number' ||
          typeof value === 'boolean'
        ) {
          return value;
        }
        return cellToString(value);
      }),
    );
    return writeXlsxFile(rows).toBlob();
  };

  const buildTxtBlob = () => {
    const escapeCell = (value: unknown) =>
      cellToString(value)
        .replaceAll('\t', ' ')
        .replaceAll('\n', ' ')
        .replaceAll('\r', ' ');
    const content = buildRows()
      .map((row) => row.map(escapeCell).join('\t'))
      .join('\n');
    return new Blob([content], { type: 'text/plain;charset=UTF-8' });
  };

  const buildCsvBlob = () => {
    const escapeCell = (value: unknown) => {
      const stringValue = cellToString(value);
      return /[",\n\r]/.test(stringValue)
        ? `"${stringValue.replaceAll('"', '""')}"`
        : stringValue;
    };
    const content = buildRows()
      .map((row) => row.map(escapeCell).join(','))
      .join('\r\n');
    return new Blob([content], { type: 'text/csv;charset=UTF-8' });
  };

  const buildBlob = async () => {
    if (format === 'xlsx') return buildXlsxBlob();
    if (format === 'csv') return buildCsvBlob();
    return buildTxtBlob();
  };

  const exportTo = async () => {
    onLoading?.(true);
    try {
      const blob = await buildBlob();
      FileSaver.saveAs(blob, `${filename}.${format}`);
    } catch (error: unknown) {
      showMessageError({ context: 'ExportToFormat.exportTo', error });
    } finally {
      onLoading?.(false);
    }
  };

  return cloneElement(children, { onClick: () => void exportTo() });
};

export default ExportToFormat;
